import { useEffect, useState } from 'react';
import ErrorUtils from "src/utils/ErrorUtils";
import { parseExcelFromArrayBuffer, ExcelRowConverter } from "src/utils/ExcelUtils";
import { MultiAllocationSheetValidationData, MultiAllocationSheetValidationErrorData } from "src/models/multidimensional-allocation/MultiAllocationSheetDetails";

/**
 * Excel cell data with optional validation information
 */
export interface ExcelCellData {
    value: string | number | undefined;
    error?: string;
}

/**
 * Excel row data with array-based cell structure
 */
export interface ExcelRowData {
    cells: ExcelCellData[];
    rowIndex: number;
    error?: string;
}

export interface ExcelHeaderData {
    value: string;
    error?: string;
}

/**
 * Create validation-aware converter that incorporates validation errors
 * 
 * @param validationData - Validation data containing error information for cells and rows
 * @returns ExcelRowConverter function that processes Excel rows with validation
 */
const createMDAExcelConverter = (validationData?: MultiAllocationSheetValidationData): ExcelRowConverter<ExcelRowData> => {
    // Create lookup maps for different error types
    const cellErrorMap = new Map<string, string>();
    const rowErrorMap = new Map<number, string>();
    
    if (validationData?.validationErrors) {
        validationData.validationErrors.forEach(error => {
             if (error?.column === -1 && error?.row !== undefined) {
                // Row-level error
                rowErrorMap.set(error.row, error.message);
            } else if (error.row !== undefined && error.column !== undefined) {
                // Cell-level error
                const key = `${error.row}-${error.column}`;
                cellErrorMap.set(key, error.message);
            }
        });
    }

    return (rowData: any[], headers: string[], rowIndex: number): ExcelRowData => {
        // Process each cell with validation information
        const cells: ExcelCellData[] = headers.map((header, columnIndex) => {
            const cellValue = rowData[columnIndex];
            const errorKey = `${rowIndex}-${columnIndex}`;
            const cellError = cellErrorMap.get(errorKey);
            
            // Handle different data types and create ExcelCellData with validation
            if (cellValue === null || cellValue === undefined) {
                return {
                    value: undefined,
                    error: cellError
                };
            } 
            return {
                    value: cellValue,
                    error: cellError
                };
            }
        );

        // Get row-level error using lookup map
        const rowError = rowErrorMap.get(rowIndex);

        return {
            cells,
            rowIndex,
            error: rowError
        };
    };
};

/**
 * MDA-specific Excel data hook with built-in validation logic
 * 
 * @param preSignedUrl - The pre-signed URL to fetch the Excel file from
 * @param validationSummary - Validation data containing error information for cells and rows
 * @returns Object containing excel data, headers, loading state, and error message
 */
export const useMDAExcelData = (preSignedUrl?: string, validationSummary?: MultiAllocationSheetValidationData) => {
    const [excelData, setExcelData] = useState<ExcelRowData[]>([]);
    const [headers, setHeaders] = useState<ExcelHeaderData[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (!preSignedUrl) {
            return;
        }

        const fetchAndParseExcel = async () => {
            setLoading(true);
            setError('');
            
            try {
                // Fetch the Excel file from the preSignedUrl
                const response = await fetch(preSignedUrl);
                if (!response.ok) {
                    throw new Error(`Failed to load excel sheet, received http: ${response.status}`);
                }

                // Get the file as array buffer
                const arrayBuffer = await response.arrayBuffer();
                
                // Parse Excel using MDA-specific converter
                const parseResult = parseExcelFromArrayBuffer<ExcelRowData>(
                    arrayBuffer,
                    createMDAExcelConverter(validationSummary),
                    {   // parser options:
                        defaultValue: ""
                    }
                );
                
                if (parseResult.headers.length === 0) {
                    setExcelData([]);
                    setHeaders([]);
                    return;
                }

                // Create column error map for headers
                const columnErrorMap = new Map<number, string>();
                if (validationSummary?.validationErrors) {
                    validationSummary.validationErrors.forEach(error => {
                        if (error?.row === undefined && error?.column !== undefined) {
                            // Column-level error
                            columnErrorMap.set(error.column, error.message);
                        }
                    });
                }

                // Transform headers to include validation information
                const headerData: ExcelHeaderData[] = parseResult.headers.map((headerValue, columnIndex) => {
                    const columnError = columnErrorMap.get(columnIndex);

                    return {
                        value: headerValue,
                        error: columnError
                    };
                });

                setHeaders(headerData);
                setExcelData(parseResult.data);
                console.log("excel data:", parseResult.data);
            } catch (err) {
                setError(ErrorUtils.getMessage(err));
            } finally {
                setLoading(false);
            }
        };

        fetchAndParseExcel();
    }, [preSignedUrl, validationSummary]);

    return {
        excelData,
        headers,
        loading,
        error
    };
};
